(() => {
  if (window.__bitcoQuickViewBound) return;
  window.__bitcoQuickViewBound = true;

  const SECTION_ID = 'bitco-quick-view';
  const CONTENT_SELECTOR = '[data-bitco-quick-view-content]';

  // Inlined to match snippets/bitco-icon.liquid's 'close' case exactly — the dialog shell is
  // built here in JS (it has no host section of its own, unlike the fetched buy-box content), so
  // it can't call the `bitco-icon` Liquid snippet.
  const CLOSE_ICON_SVG =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';

  let dialog = null;
  let body = null;
  let unsubscribeCartUpdate = null;

  const closeLabel = () => (window.accessibilityStrings && window.accessibilityStrings.close) || 'Close';
  const errorMessage = () => (window.cartStrings && window.cartStrings.error) || 'Something went wrong, please try again.';

  const stopWatchingCart = () => {
    if (unsubscribeCartUpdate) {
      unsubscribeCartUpdate();
      unsubscribeCartUpdate = null;
    }
  };

  // Dawn's product-form.js (loaded globally, see layout/theme.liquid) publishes
  // PUB_SUB_EVENTS.cartUpdate right after a successful AJAX add — closing the quick view then
  // means the shopper lands on the cart drawer instead of two overlays stacked on top of each
  // other. assets/pubsub.js + assets/constants.js are already loaded globally ahead of this
  // deferred script, so subscribe/PUB_SUB_EVENTS are always defined here — no MutationObserver
  // fallback needed.
  const watchCartUpdate = () => {
    stopWatchingCart();
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (dialog && dialog.open) dialog.close();
    });
  };

  const ensureDialog = () => {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'bitco-qv';

    const panel = document.createElement('div');
    panel.className = 'bitco-qv__panel';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'bitco-qv__close';
    closeButton.setAttribute('aria-label', closeLabel());
    closeButton.innerHTML = CLOSE_ICON_SVG;
    closeButton.addEventListener('click', () => dialog.close());

    body = document.createElement('div');
    body.className = 'bitco-qv__body';

    panel.appendChild(closeButton);
    panel.appendChild(body);
    dialog.appendChild(panel);
    document.body.appendChild(dialog);

    // Backdrop click: a click that lands on the <dialog> element itself (its padding box, outside
    // `.bitco-qv__panel`) rather than on any descendant.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    // Native <dialog> already handles Esc. Clear content on every close (backdrop/Esc/X/cart-
    // update) so a stale product never flashes before the next fetch's loading state paints.
    dialog.addEventListener('close', () => {
      body.innerHTML = '';
      stopWatchingCart();
    });

    return dialog;
  };

  const showLoading = () => {
    body.innerHTML = '<div class="bitco-qv__loading" aria-hidden="true"><span class="bitco-qv__spinner"></span></div>';
  };

  const showError = () => {
    body.innerHTML = '<p class="bitco-qv__load-error">' + errorMessage() + '</p>';
  };

  // Server-rendered price blocks already exist per variant (sections/bitco-quick-view.liquid) so
  // bitco-vat.js's incl/excl spans work immediately; this only toggles which variant's block is
  // visible and mirrors the selected option's availability onto the submit button — no section
  // re-render, matching the brief's "no product-info.js inside the modal" constraint.
  // Jump the gallery straight to one image (instant: a smooth scroll races mandatory snap).
  const showMedia = (root, mediaId) => {
    const gallery = root.querySelector('[data-qv-gallery]');
    if (!gallery || !mediaId) return;
    const slides = Array.prototype.slice.call(gallery.children);
    const index = slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
    if (index >= 0) gallery.scrollTo({ left: index * gallery.clientWidth });
  };

  // Photo counter ("2 / 7") and arrow state for the gallery, in the modal and the hover preview.
  const bindGallery = (root) => {
    const gallery = root.querySelector('[data-qv-gallery]');
    if (!gallery) return;
    const counter = root.querySelector('[data-qv-counter]');
    const prev = root.querySelector('[data-qv-prev]');
    const next = root.querySelector('[data-qv-next]');
    const total = gallery.children.length;
    const update = () => {
      const index = Math.round(gallery.scrollLeft / (gallery.clientWidth || 1));
      if (counter) counter.textContent = index + 1 + ' / ' + total;
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= total - 1;
    };
    gallery.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    update();
  };

  const bindVariantSelect = (root) => {
    const select = root.querySelector('[data-qv-variant-select]');
    if (!select) return;
    const submitButton = root.querySelector('product-form [type="submit"]');
    const priceBlocks = root.querySelectorAll('[data-qv-price]');
    const detailsLink = root.querySelector('[data-qv-details]');
    const productUrl = detailsLink ? detailsLink.getAttribute('href') : null;

    const sync = () => {
      const option = select.options[select.selectedIndex];
      const variantId = option ? option.getAttribute('data-variant-id') : null;

      // M1.6: on a multi-variant product the select opens on a placeholder with no
      // data-variant-id, so fall back to the default variant's price block — otherwise every
      // block would be hidden and the sheet would open with no price at all.
      const displayId = variantId || select.dataset.defaultVariantId || null;
      priceBlocks.forEach((priceEl) => {
        priceEl.hidden = priceEl.getAttribute('data-variant-id') !== displayId;
      });

      if (submitButton) {
        // No variantId means the placeholder is still selected: nothing to add yet.
        submitButton.disabled = !option || option.disabled || !variantId;
      }
      if (detailsLink && productUrl) {
        detailsLink.setAttribute('href', variantId ? productUrl + '?variant=' + variantId : productUrl);
      }
    };

    // Colour swatches (sections/bitco-quick-view.liquid): the dropdown lists only the chosen
    // colour's sizes. The full option list is kept in memory and the <select name="id"> is
    // rebuilt per colour, because hidden <option>s are not reliably hidden in every browser.
    const swatches = root.querySelectorAll('[data-qv-color]');
    if (swatches.length) {
      const placeholder = select.querySelector('[data-placeholder]');
      const soldOut = select.getAttribute('data-soldout') || '';
      const variants = Array.prototype.filter.call(select.options, (o) => o.hasAttribute('data-variant-id')).map((o) => ({
        id: o.getAttribute('data-variant-id'),
        color: o.getAttribute('data-color'),
        label: o.getAttribute('data-label') || '',
        disabled: o.disabled,
      }));
      const colorName = root.querySelector('[data-qv-color-name]');

      const chooseColor = (swatch, moveGallery) => {
        const color = swatch.getAttribute('data-qv-color');
        swatches.forEach((s) => s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false'));
        if (colorName) colorName.textContent = color;
        const forColor = variants.filter((v) => v.color === color);
        select.innerHTML = '';
        if (placeholder && forColor.length > 1) {
          placeholder.selected = true;
          select.appendChild(placeholder);
        }
        forColor.forEach((v) => {
          const option = document.createElement('option');
          option.value = v.id;
          option.setAttribute('data-variant-id', v.id);
          option.textContent = (v.label || color) + (v.disabled ? soldOut : '');
          option.disabled = v.disabled;
          select.appendChild(option);
        });
        // One variant in this colour (e.g. one-size items): select it, nothing left to choose.
        if (forColor.length === 1) select.value = forColor[0].id;
        select.hidden = forColor.length === 1 && !forColor[0].label;
        const firstAvailable = forColor.find((v) => !v.disabled) || forColor[0];
        if (firstAvailable) select.dataset.defaultVariantId = firstAvailable.id;
        if (moveGallery) showMedia(root, swatch.getAttribute('data-media-id'));
        sync();
      };

      swatches.forEach((swatch) => swatch.addEventListener('click', () => chooseColor(swatch, true)));
      const initial = root.querySelector('[data-qv-color][aria-pressed="true"]') || swatches[0];
      // The gallery opens on the product's main photo; only move it once the shopper picks.
      chooseColor(initial, false);
    }

    select.addEventListener('change', sync);
    sync();
  };

  // One request per product URL, shared by the hover preview and the modal: hovering a card
  // prefetches, so opening the modal (or re-hovering) afterwards is instant. A failed request is
  // dropped from the cache so the next attempt retries. Each caller gets a freshly parsed node,
  // because appending the node moves it.
  const sectionCache = new Map();
  const fetchSection = (url) => {
    if (!sectionCache.has(url)) {
      const separator = url.indexOf('?') === -1 ? '?' : '&';
      const request = fetch(url + separator + 'section_id=' + SECTION_ID)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error('bad response'))))
        .catch((error) => {
          sectionCache.delete(url);
          throw error;
        });
      sectionCache.set(url, request);
    }
    return sectionCache.get(url);
  };
  const parseContent = (text) => new DOMParser().parseFromString(text, 'text/html').querySelector(CONTENT_SELECTOR);

  const open = (url) => {
    if (window.bitcoPeek) window.bitcoPeek.close();
    const dlg = ensureDialog();
    showLoading();
    if (!dlg.open) dlg.showModal();

    fetchSection(url)
      .then((text) => {
        if (!dlg.open) return; // closed while the fetch was in flight
        const content = parseContent(text);
        if (!content) {
          showError();
          return;
        }
        body.innerHTML = '';
        body.appendChild(content);
        bindVariantSelect(body);
        bindGallery(body);
        watchCartUpdate();
      })
      .catch(() => {
        if (dlg.open) showError();
      });
  };

  // Delegated on document, never bound per-card: facets.js re-renders the collection grid via
  // section rendering on every filter/sort change, replacing card DOM wholesale, so a listener
  // bound directly to a card's button would go stale the moment the grid re-renders. The gallery
  // arrows are delegated for the same reason — the dialog body is re-injected on every open.
  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-qv-prev], [data-qv-next]');
    const arrowScope = arrow && arrow.closest(CONTENT_SELECTOR);
    if (arrowScope) {
      const gallery = arrowScope.querySelector('[data-qv-gallery]');
      if (gallery) {
        // Instant, index-based jump. A smooth scrollBy raced Chrome's mandatory
        // scroll-snap: interrupted below the halfway point it snapped BACK to
        // the current slide, so arrows appeared dead.
        const direction = arrow.hasAttribute('data-qv-prev') ? -1 : 1;
        const width = gallery.clientWidth;
        const target = (Math.round(gallery.scrollLeft / width) + direction) * width;
        gallery.scrollTo({ left: Math.max(0, Math.min(target, gallery.scrollWidth - width)) });
      }
      return;
    }

    const trigger = event.target.closest('[data-bitco-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    const url = trigger.getAttribute('data-product-url');
    if (!url) return;
    open(url);
  });
  /* Hover preview ("peek"). Resting the mouse on a product card for a moment grows the card in
     place into a larger, interactive version of itself: photo gallery with arrows, colour swatches,
     size picker, add to cart. It sits over the card (not beside it), so it reads as the same card,
     and clicking anywhere on it except a control opens the product page, like the card. It is not
     modal: no backdrop, the page keeps scrolling, and it closes once the pointer leaves it.
     Mouse-driven desktops only; touch and keyboard keep the eye button, which opens the modal.

     Everything is decided from pointermove targets rather than enter/leave pairs, because the grid
     is re-rendered by facets.js and carousels move cards under a still pointer. */
  const PEEK_OPEN_DELAY = 450; // dwell before opening, so sweeping across a grid opens nothing
  const PEEK_PREFETCH_DELAY = 150;
  const PEEK_CLOSE_DELAY = 250; // time to cross the gap between card and panel
  const PEEK_EDGE = 12; // min distance from the viewport edge
  const PEEK_GROW = 36; // how far the expanded card reaches past the card on each side
  const PEEK_MIN_WIDTH = 380;
  const PEEK_MAX_WIDTH = 480;
  const CARD_SELECTOR = '.bitco-product-card';
  const canPeek = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

  let peek = null;
  let peekBody = null;
  let peekCard = null;
  let pendingCard = null;
  let openTimer = 0;
  let prefetchTimer = 0;
  let closeTimer = 0;
  let frame = 0;
  let selectGuardSince = 0; // a native dropdown in the panel was opened at this time
  let peekToken = 0;

  const cardUrl = (card) => {
    const trigger = card.querySelector('[data-bitco-quick-view]');
    return trigger ? trigger.getAttribute('data-product-url') : null;
  };

  // Something else already owns the screen: the modal quick view, the cart drawer, the menu drawer.
  const blocked = () =>
    !!document.querySelector('dialog[open], cart-drawer.active, .bitco-drawer:not([hidden]), details[data-bitco-mega][open]');

  const ensurePeek = () => {
    if (peek) return peek;
    peek = document.createElement('div');
    peek.className = 'bitco-peek';
    peek.setAttribute('role', 'dialog');
    peek.setAttribute('aria-modal', 'false');
    peek.hidden = true;
    peekBody = document.createElement('div');
    peekBody.className = 'bitco-peek__body';
    peek.appendChild(peekBody);
    document.body.appendChild(peek);

    // A native <select> dropdown can hang outside the panel, and while it is open the page gets
    // no pointer events at all. So once one is opened, ignore the pointer until it moves again:
    // the first pointermove after that means the dropdown has closed.
    peek.addEventListener('pointerdown', (event) => {
      if (event.target.closest('select')) selectGuardSince = Date.now();
    });
    // The expanded card behaves like the card: a click anywhere that is not a control opens the
    // product page (the chosen variant, once one is picked). Cmd/Ctrl-click opens a new tab.
    peek.addEventListener('click', (event) => {
      if (event.target.closest('a, button, select, input, label, textarea, .bitco-qv__form')) return;
      const link = peek.querySelector('[data-qv-details]');
      if (!link) return;
      if (event.metaKey || event.ctrlKey) window.open(link.href, '_blank');
      else window.location.href = link.href;
    });
    return peek;
  };

  const place = () => {
    if (!peek || peek.hidden || !peekCard) return;
    if (!peekCard.isConnected) { closePeek(); return; }
    const rect = peekCard.getBoundingClientRect();
    const viewportW = document.documentElement.clientWidth;
    const viewportH = window.innerHeight;
    // The card scrolled or slid (carousel autoplay) out of view: nothing left to point at.
    if (rect.bottom < 0 || rect.top > viewportH || rect.right < 0 || rect.left > viewportW) {
      closePeek();
      return;
    }
    // Centred on the card and a little larger on every side, so it reads as the card growing.
    const width = Math.round(Math.min(PEEK_MAX_WIDTH, Math.max(PEEK_MIN_WIDTH, rect.width + 2 * PEEK_GROW)));
    if (peek.style.width !== width + 'px') peek.style.width = width + 'px';
    const height = peek.offsetHeight;
    const left = Math.max(PEEK_EDGE, Math.min(viewportW - width - PEEK_EDGE, rect.left + (rect.width - width) / 2));
    const top = Math.max(PEEK_EDGE, Math.min(rect.top - PEEK_GROW / 2, viewportH - height - PEEK_EDGE));
    peek.style.left = Math.round(left) + 'px';
    peek.style.top = Math.round(top) + 'px';
    // Grow out of the card: the entry animation scales from the card's own size and centre.
    peek.style.transformOrigin = Math.round(rect.left + rect.width / 2 - left) + 'px ' + Math.round(rect.top + rect.height / 2 - top) + 'px';
    peek.style.setProperty('--peek-from', (rect.width / width).toFixed(3));
  };

  const track = () => {
    place();
    if (peek && !peek.hidden) frame = window.requestAnimationFrame(track);
  };

  const cancelClose = () => {
    window.clearTimeout(closeTimer);
    closeTimer = 0;
  };

  const cancelPending = () => {
    window.clearTimeout(openTimer);
    window.clearTimeout(prefetchTimer);
    pendingCard = null;
  };

  function closePeek() {
    cancelClose();
    cancelPending();
    window.cancelAnimationFrame(frame);
    peekToken += 1;
    selectGuardSince = 0;
    if (!peek || peek.hidden) { peekCard = null; return; }
    peek.hidden = true;
    peek.classList.remove('is-visible');
    peekBody.innerHTML = '';
    if (peekCard) peekCard.classList.remove('is-peeking');
    peekCard = null;
  }

  const scheduleClose = () => {
    if (closeTimer || selectGuardSince) return;
    closeTimer = window.setTimeout(closePeek, PEEK_CLOSE_DELAY);
  };

  const openPeek = (card) => {
    const url = cardUrl(card);
    if (!url || blocked()) return;
    ensurePeek();
    if (peekCard && peekCard !== card) peekCard.classList.remove('is-peeking');
    peekCard = card;
    card.classList.add('is-peeking');
    const token = ++peekToken;
    const title = card.querySelector('.bitco-product-card__title');
    peek.setAttribute('aria-label', title ? title.textContent.trim() : '');

    // Shown only once the content is in (usually already prefetched), so the card grows straight
    // into the finished preview instead of flashing a spinner.
    fetchSection(url)
      .then((text) => {
        if (token !== peekToken) return; // closed or moved to another card meanwhile
        const content = parseContent(text);
        if (!content) { closePeek(); return; }
        peek.classList.remove('is-visible');
        peekBody.innerHTML = '';
        peekBody.appendChild(content);
        bindVariantSelect(peekBody);
        bindGallery(peekBody);
        peek.hidden = false;
        place();
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => peek.classList.add('is-visible')));
        window.cancelAnimationFrame(frame);
        track();
      })
      .catch(() => { if (token === peekToken) closePeek(); });
  };

  document.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse' || !canPeek.matches) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (selectGuardSince) {
      if (Date.now() - selectGuardSince < 200) return; // the press that opened the dropdown
      selectGuardSince = 0;
    }
    const inPeek = !!(peek && !peek.hidden && peek.contains(target));
    const card = inPeek ? null : target.closest(CARD_SELECTOR);

    if (peekCard) {
      if (inPeek || card === peekCard) {
        cancelClose();
      } else {
        scheduleClose();
      }
    }

    if (inPeek || card === peekCard) {
      cancelPending();
      return;
    }
    if (!card) {
      cancelPending();
      return;
    }
    if (card === pendingCard) return;
    cancelPending();
    pendingCard = card;
    prefetchTimer = window.setTimeout(() => {
      const url = cardUrl(card);
      if (url) fetchSection(url).catch(() => {});
    }, PEEK_PREFETCH_DELAY);
    openTimer = window.setTimeout(() => {
      pendingCard = null;
      openPeek(card);
    }, PEEK_OPEN_DELAY);
  }, { passive: true });

  document.documentElement.addEventListener('mouseleave', () => {
    cancelPending();
    if (peekCard) scheduleClose();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    // Esc on an open size dropdown only closes the dropdown.
    const active = document.activeElement;
    if (peek && active && active.tagName === 'SELECT' && peek.contains(active)) return;
    closePeek();
  });
  // A click on a card (navigating) or anywhere outside the panel ends the preview.
  document.addEventListener('pointerdown', (event) => {
    if (peek && !peek.hidden && !peek.contains(event.target)) closePeek();
  });
  window.addEventListener('resize', closePeek);
  window.addEventListener('pagehide', closePeek);
  canPeek.addEventListener('change', closePeek);
  // Added to cart: the cart drawer takes over, same as the modal.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, closePeek);
  }

  window.bitcoPeek = { close: closePeek };
})();
